// components/EmailConnection.jsx
import { useState, useEffect } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { useMsal } from '@azure/msal-react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import {
    Box,
    Button,
    Typography,
    Card,
    CardContent,
    Stack,
    Chip,
    Alert,
    Snackbar,
} from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';

import MicrosoftIcon from '@mui/icons-material/Microsoft';



export const EmailConnection = () => {
    const { user } = useAuth();
    const { instance, accounts } = useMsal();
    const [connectedAccounts, setConnectedAccounts] = useState({
        gmail: null,
        outlook: null,
        zoho: null
    });
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const loadConnectedAccounts = async () => {
            if (user?.email) {
                const docRef = doc(db, 'users', user.email);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const userData = docSnap.data();
                    setConnectedAccounts(prev => ({
                        ...prev,
                        gmail: userData.gmailEmail || userData.connectedEmail || null,
                        outlook: userData.outlookEmail || null
                    }));
                }
            }
        };
        loadConnectedAccounts();
    }, [user]);

    const login = useGoogleLogin({
        flow: 'auth-code',
        scope: 'https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.modify https://www.googleapis.com/auth/gmail.compose',
        redirect_uri: 'postmessage',  // Add this line
        onSuccess: async (codeResponse) => {
            try {
                // Exchange code for tokens
                const tokensResponse = await fetch(`${process.env.REACT_APP_BACK_END_URL}/applybridge/auth/google/callback`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        code: codeResponse.code
                    })
                });

                if (!tokensResponse.ok) {
                    const errorData = await tokensResponse.json();
                    console.error('Token exchange error:', errorData);
                    throw new Error(errorData.detail || 'Failed to exchange code for tokens');
                }

                const tokens = await tokensResponse.json();

                // Get user info using the access token
                const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
                    headers: { Authorization: `Bearer ${tokens.access_token}` }
                });

                if (!userInfoResponse.ok) {
                    throw new Error('Failed to get user info');
                }

                const userInfo = await userInfoResponse.json();

                // Update user document with tokens and email
                await updateDoc(doc(db, 'users', user.email), {
                    gmailToken: tokens.access_token,
                    gmailRefreshToken: tokens.refresh_token,
                    tokenExpiresAt: new Date(Date.now() + (tokens.expires_in * 1000)).toISOString(),
                    gmailEmail: userInfo.email,  // Store Gmail address
                    connectedEmail: userInfo.email // Keep this for backward compatibility
                });

                setConnectedAccounts(prev => ({
                    ...prev,
                    gmail: userInfo.email
                }));
                setSuccess(true);
            } catch (err) {
                console.error('Error during Google auth:', err);
                setError('Failed to connect Gmail account: ' + err.message);
            }
        },
        onError: (error) => {
            console.error('Google Login Error:', error);
            setError('Failed to connect with Google');
        }
    });

    useEffect(() => {
        checkOutlookAuth();
    }, []);

    useEffect(() => {
        console.log('Connected accounts updated:', connectedAccounts);
    }, [connectedAccounts]);




    const checkOutlookAuth = async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACK_END_URL}/applybridge/auth/outlook/check`,
                {
                    credentials: 'include',
                    headers: {
                        'Accept': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Auth check failed');
            }

            const data = await response.json();
            console.log("Out look Check data : ", data);
            setConnectedAccounts(prevState => ({
                ...prevState,
                outlook: data.isAuthenticated ? 'Connected' : null
            }));
        } catch (err) {
            console.error('Error checking auth:', err);
            setError('Failed to verify Outlook connection');
        }
    };

    const handleOutlookLogin = async () => {
        try {
            console.log("Starting Outlook login process");

            const handleCallback = async (event) => {
                console.log("DEBUG - Message received:", {
                    origin: event.origin,
                    data: event.data,
                    frontendUrl: window.location.origin
                });

                // Accept messages from any of our domains
                const allowedOrigins = [
                    process.env.REACT_APP_BACK_END_URL,
                    'https://apply-bridge-backend-91ee748b332f.herokuapp.com',
                    'https://www.apply-bridge.com',
                    'https://apply-bridge.com'
                ];

                if (!allowedOrigins.includes(event.origin)) {
                    console.log('Message from unexpected origin:', event.origin);
                    return;
                }

                try {
                    const { success, tokens } = event.data;
                    console.log("Message parsed:", { success, hasTokens: !!tokens });

                    if (success && tokens) {
                        console.log("Processing successful authentication");

                        // Get user info from Microsoft Graph
                        const userInfoResponse = await fetch('https://graph.microsoft.com/v1.0/me', {
                            headers: {
                                'Authorization': `Bearer ${tokens.access_token}`,
                                'Content-Type': 'application/json'
                            }
                        });

                        if (!userInfoResponse.ok) {
                            throw new Error('Failed to get Outlook user info');
                        }

                        const userInfo = await userInfoResponse.json();
                        const outlookEmail = userInfo.mail || userInfo.userPrincipalName;

                        const storeResponse = await fetch(
                            `${process.env.REACT_APP_BACK_END_URL}/applybridge/auth/outlook/callback`,
                            {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                },
                                body: JSON.stringify({
                                    user_email: user.email,
                                    tokens: tokens
                                })
                            }
                        );

                        if (storeResponse.ok) {
                            console.log("Successfully stored tokens");

                            // Update Firebase with Outlook email
                            await updateDoc(doc(db, 'users', user.email), {
                                outlookAccessToken: tokens.access_token,
                                outlookRefreshToken: tokens.refresh_token,
                                outlookTokenExpiresAt: new Date(Date.now() + (tokens.expires_in * 1000)).toISOString(),
                                outlookEmail: outlookEmail,
                                outlookConnected: true
                            });

                            setConnectedAccounts(prev => ({
                                ...prev,
                                outlook: outlookEmail  // Store the actual email instead of just 'Connected'
                            }));
                            setSuccess(true);
                        } else {
                            console.error("Failed to store tokens:", await storeResponse.text());
                        }
                    }
                } catch (error) {
                    console.error('Error processing message:', error);
                }
            };

            console.log("Adding message listener");
            window.addEventListener('message', handleCallback);

            const response = await fetch(
                `${process.env.REACT_APP_BACK_END_URL}/applybridge/auth/outlook/login`
            );
            const { authUrl } = await response.json();
            console.log("Opening popup with URL:", authUrl);

            window.open(authUrl, 'outlook-auth', 'width=600,height=600,scrollbars=yes');

        } catch (error) {
            console.error('Outlook auth error:', error);
            setError('Failed to initiate authentication');
        }
    };

    const handleOutlookLogout = async () => {
        try {
            await fetch(`${process.env.REACT_APP_BACK_END_URL}/applybridge/auth/outlook/logout`, {
                method: 'POST',
                credentials: 'include'
            });
            setConnectedAccounts(prev => ({
                ...prev,
                outlook: null
            }));
            setSuccess(true);
        } catch (err) {
            setError('Failed to disconnect Outlook account');
        }
    };



    const handleDisconnect = async (service) => {
        try {
            if (service === 'outlook') {
                instance.logoutPopup();
            }

            const updates = {};
            if (service === 'gmail') {
                updates.gmailToken = null;
                updates.connectedEmail = null;
            } else if (service === 'outlook') {
                updates.outlookToken = null;
                updates.outlookEmail = null;
            }

            await updateDoc(doc(db, 'users', user.email), updates);
            setConnectedAccounts(prev => ({
                ...prev,
                [service]: null
            }));
            setSuccess(true);
        } catch (err) {
            setError(`Failed to disconnect ${service} account`);
        }
    };



    return (
        <Card>
            <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="h6">
                        Connected Email Accounts
                    </Typography>
                    <Stack direction="row" spacing={2}>
                        {/* Gmail Connection */}
                        {connectedAccounts.gmail ? (
                            <Chip
                                icon={<GoogleIcon />}
                                label={connectedAccounts.gmail}
                                color="primary"
                                onDelete={() => handleDisconnect('gmail')}
                            />
                        ) : (
                            <Button
                                variant="outlined"
                                startIcon={<GoogleIcon />}
                                onClick={() => login()}
                                sx={{
                                    borderColor: '#0078D4',
                                    color: '#0078D4',
                                    '&:hover': {
                                        borderColor: '#0078D4',
                                        backgroundColor: 'rgba(0, 120, 212, 0.04)'
                                    }
                                }}
                            >
                                Connect Gmail Account
                            </Button>
                        )}
                        {/* Outlook Connection */}
                        {connectedAccounts.outlook ? (
                            <Chip
                                icon={<MicrosoftIcon />}
                                label={connectedAccounts.outlook}  // Now shows the actual email
                                color="primary"
                                onDelete={handleOutlookLogout}
                            />
                        ) : (
                            <Button
                                variant="outlined"
                                startIcon={<MicrosoftIcon />}
                                onClick={handleOutlookLogin}
                                sx={{
                                    borderColor: '#0078D4',
                                    color: '#0078D4',
                                    '&:hover': {
                                        borderColor: '#0078D4',
                                        backgroundColor: 'rgba(0, 120, 212, 0.04)'
                                    }
                                }}
                            >
                                Connect Outlook Account
                            </Button>
                        )}
                    </Stack>
                </Box>
            </CardContent>
            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError(null)}
            >
                <Alert severity="error" variant="filled">
                    {error}
                </Alert>
            </Snackbar>
            <Snackbar
                open={success}
                autoHideDuration={6000}
                onClose={() => setSuccess(false)}
            >
                <Alert severity="success" variant="filled">
                    Email account updated successfully!
                </Alert>
            </Snackbar>
        </Card>
    );
};